import { supabase } from '../lib/supabase';
import { trackWishlistAdd, trackWishlistRemove } from './userJourneyService';
import { sendWishlistSavedEmail } from './resendEmailService';

export const wishlistService = {
  /**
   * Add a property to the user's wishlist
   */
  async addToWishlist(userId, property, userProfile = null) {
    const { data, error } = await supabase
      ?.from('user_wishlist')
      ?.insert({
        user_id: userId,
        property_id: property?.id?.toString(),
        property_title: property?.title || property?.custom_headline || '',property_price: parseInt(property?.price || property?.promotional_price || 0) || null,property_commune: property?.commune || property?.location || property?.commune_name || '',property_image: property?.image || property?.thumbnail || property?.hero_image_url || '',property_type: property?.type || property?.property_type || '',market_type: property?.marketType || property?.market_type || 'sale',
      })
      ?.select()
      ?.single();

    if (!error) {
      trackWishlistAdd(property);

      // Send wishlist saved email (non-blocking, respects communication preferences)
      const userEmail = userProfile?.email || null;
      const userName = userProfile?.full_name || userProfile?.email || null;
      if (userId && userEmail) {
        sendWishlistSavedEmail({
          user_id: userId,
          user_name: userName,
          user_email: userEmail,
          property_title: property?.title || property?.custom_headline || '',property_price: parseInt(property?.price || property?.promotional_price || 0) || null,property_commune: property?.commune || property?.location || property?.commune_name || '',property_type: property?.type || property?.property_type || '',
          property_url: property?.id ? `https://kapitalstates.com/individual-property-page?id=${property?.id}` : null,
        });
      }
    }

    return { data, error };
  },

  /**
   * Remove a property from the user's wishlist
   */
  async removeFromWishlist(userId, propertyId) {
    const { error } = await supabase
      ?.from('user_wishlist')
      ?.delete()
      ?.eq('user_id', userId)
      ?.eq('property_id', propertyId?.toString());

    if (!error) {
      trackWishlistRemove({ id: propertyId });
    }

    return { error };
  },

  /**
   * Check if a property is in the user's wishlist
   */
  async isInWishlist(userId, propertyId) {
    if (!userId || !propertyId) return { data: false, error: null };

    const { data, error } = await supabase
      ?.from('user_wishlist')
      ?.select('id')
      ?.eq('user_id', userId)
      ?.eq('property_id', propertyId?.toString())
      ?.maybeSingle();

    return { data: !!data, error };
  },

  /**
   * Get all wishlist items for a user with optional filters
   */
  async getWishlist(userId, filters = {}) {
    if (!userId) return { data: [], error: null };

    let query = supabase
      ?.from('user_wishlist')
      ?.select('*')
      ?.eq('user_id', userId);

    // Filter by commune
    if (filters?.commune && filters?.commune !== 'all') {
      query = query?.ilike('property_commune', `%${filters?.commune}%`);
    }

    // Filter by price range
    if (filters?.minPrice) {
      query = query?.gte('property_price', parseInt(filters?.minPrice));
    }
    if (filters?.maxPrice) {
      query = query?.lte('property_price', parseInt(filters?.maxPrice));
    }

    // Sort by created_at
    const sortOrder = filters?.sortOrder === 'asc' ? true : false;
    query = query?.order('created_at', { ascending: sortOrder });

    const { data, error } = await query;
    return { data: data || [], error };
  },

  /**
   * Get all wishlist property IDs for a user (for quick lookup)
   */
  async getWishlistIds(userId) {
    if (!userId) return { data: [], error: null };

    const { data, error } = await supabase
      ?.from('user_wishlist')
      ?.select('property_id')
      ?.eq('user_id', userId);

    return { data: data?.map(item => item?.property_id) || [], error };
  },

  /**
   * Toggle wishlist status for a property
   */
  async toggleWishlist(userId, property) {
    const { data: isWishlisted } = await wishlistService?.isInWishlist(userId, property?.id);

    if (isWishlisted) {
      const { error } = await wishlistService?.removeFromWishlist(userId, property?.id);
      return { added: false, error };
    } else {
      const { data, error } = await wishlistService?.addToWishlist(userId, property);
      return { added: true, data, error };
    }
  },
};

export default wishlistService;
